/**
 * Der Bildraten-Zähler aus `PROTOTYP-PLAN.md` P5.
 *
 * Gemessen werden muss auf **Annas Gerät**, ohne Kabel und ohne Entwicklerwerkzeuge —
 * sonst gibt es am Ende eine geschätzte Zahl im Konzeptpapier statt einer gemessenen.
 * Also zählt die Seite selbst mit und zeigt das Ergebnis an (zuschaltbar mit `?messen=1`,
 * siehe `src/Bildrate.tsx`).
 *
 * Hier steht nur das Rechnen, ohne `requestAnimationFrame` und ohne React: ein Zähler,
 * dessen Ergebnis man nicht prüfen kann, ist als Beleg nichts wert.
 *
 * **Berichtet wird die niedrigste abgeschlossene Sekunde**, nicht der Durchschnitt. Ein
 * Mittelwert von 55 sagt nichts darüber, ob das Scrollen zwischendurch stockt — und
 * genau das Stocken ist, was ein Mensch „hakelig" nennt. Der Schwellwert aus P5 sind
 * 50 Bilder/s.
 */

/** Wie viele abgeschlossene Sekunden behalten werden. Fünf sind lang genug, um ein
 *  Stocken zu fangen, und kurz genug, dass eine neue Messung nicht die alte mitschleppt. */
export const HALTEN_SEKUNDEN = 5;

const SEKUNDE_MS = 1000;

export interface Bildratenstand {
  /** Beginn der laufenden Sekunde, in ms (`performance.now()`-Zeitbasis). */
  beginn: number;
  /** Rahmen, die in der laufenden Sekunde gezählt wurden. */
  rahmen: number;
  /** Die letzten abgeschlossenen Sekunden, die neueste zuletzt. */
  sekunden: number[];
}

/** Ein frischer Zähler. */
export function beginn(jetzt: number): Bildratenstand {
  return { beginn: jetzt, rahmen: 0, sekunden: [] };
}

/**
 * Einen Rahmen zählen.
 *
 * Ist die Sekunde um, wird sie abgeschlossen — und zwar **geteilt durch die wirklich
 * verstrichene Zeit**, nicht durch eine angenommene Sekunde. Der Unterschied ist der
 * interessante Fall: bleibt die Seite drei Sekunden stehen und liefert dabei drei
 * Rahmen, sind das 1 Bild/s und nicht 3. Wer durch eine feste Sekunde teilt, meldet
 * genau beim Stocken zu gute Werte.
 */
export function mitRahmen(stand: Bildratenstand, jetzt: number): Bildratenstand {
  const rahmen = stand.rahmen + 1;
  const verstrichen = jetzt - stand.beginn;
  if (verstrichen < SEKUNDE_MS) return { ...stand, rahmen };

  const gemessen = Math.round((rahmen / verstrichen) * SEKUNDE_MS);
  return {
    beginn: jetzt,
    rahmen: 0,
    sekunden: [...stand.sekunden, gemessen].slice(-HALTEN_SEKUNDEN),
  };
}

/** Die niedrigste abgeschlossene Sekunde, oder `null`, solange keine vollständig ist. */
export function niedrigste(stand: Bildratenstand): number | null {
  if (stand.sekunden.length === 0) return null;
  return Math.min(...stand.sekunden);
}

/** Die zuletzt abgeschlossene Sekunde, oder `null`. Steht neben der niedrigsten, damit
 *  man beim Messen sieht, ob es gerade noch stockt oder nur einmal gestockt hat. */
export function zuletzt(stand: Bildratenstand): number | null {
  return stand.sekunden.at(-1) ?? null;
}
